import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import Map, { Marker, Popup } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  fetchNearbyPinsRequest,
  setUserLocation,
  setMapCenter,
  selectPin,
  clearSelectedPin
} from '../redux/map/actions';
import { playTrackRequest } from '../redux/audio/actions';
import { logout } from '../redux/auth/actions';
import { doFetch, clearMe } from '../redux/api/actions';
import { ME } from '../redux/api/constants';
import SearchBar from './SearchBar';
import MapAddTrackModal from './MapAddTrackModal';
import './MapView.css';

// Mapbox access token (you'll need to set this in environment variables)
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoidG9yZ3lkIiwiYSI6ImNtNXN6ZGQ0bjBhbGIya3M5dHpwY2p3aDkifQ.placeholder';

const MapView = () => {
  const dispatch = useDispatch();
  const mapRef = useRef();
  const [searchParams] = useSearchParams();

  const paramLat = parseFloat(searchParams.get('lat'));
  const paramLng = parseFloat(searchParams.get('lng'));
  const paramZoom = parseFloat(searchParams.get('zoom'));
  const hasLocationParams = !isNaN(paramLat) && !isNaN(paramLng);

  // Redux state
  const {
    pins,
    userLocation,
    selectedPin,
    pinsLoading
  } = useSelector(state => state.map);

  const accessToken = useSelector(state => state.Auth.accessToken);
  const me = useSelector(state => state.API.me);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Pin dropping state (click without drag)
  const [tempPin, setTempPin] = useState(null);
  const [showAddTrackModal, setShowAddTrackModal] = useState(false);
  const clickCoords = useRef(null);
  const isDragging = useRef(false);
  const mouseDownPos = useRef(null);

  // Local state for viewport
  const [viewState, setViewState] = useState({
    longitude: hasLocationParams ? paramLng : -74.006,
    latitude: hasLocationParams ? paramLat : 40.7128,
    zoom: hasLocationParams ? (paramZoom || 15) : 13
  });

  // Fetch user profile when access token changes
  useEffect(() => {
    if (accessToken) {
      dispatch(doFetch(ME, 'users/me', accessToken));
    } else {
      dispatch(clearMe());
    }
  }, [dispatch, accessToken]);

  // Get user's geolocation on mount (skipped if URL params provided)
  useEffect(() => {
    if (hasLocationParams) {
      dispatch(fetchNearbyPinsRequest(paramLat, paramLng, 10));
      return;
    }

    const fallbackToIPGeolocation = async () => {
      try {
        // Use IP-based geolocation as fallback
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();

        if (data.latitude && data.longitude) {
          const { latitude, longitude } = data;
          console.log(`IP geolocation: ${data.city}, ${data.region}, ${data.country_name}`);

          setViewState({
            longitude,
            latitude,
            zoom: 13
          });
          dispatch(fetchNearbyPinsRequest(latitude, longitude, 10));
        } else {
          // Final fallback to NYC
          console.log('IP geolocation failed, defaulting to NYC');
          dispatch(fetchNearbyPinsRequest(40.7128, -74.006, 10));
        }
      } catch (error) {
        console.error('IP geolocation error:', error);
        // Final fallback to NYC
        dispatch(fetchNearbyPinsRequest(40.7128, -74.006, 10));
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          console.log('Browser geolocation successful');
          dispatch(setUserLocation(latitude, longitude));
          setViewState({
            longitude,
            latitude,
            zoom: 13
          });
          // Fetch nearby pins
          dispatch(fetchNearbyPinsRequest(latitude, longitude, 10));
        },
        (error) => {
          console.log('Browser geolocation denied/failed, trying IP geolocation');
          // Fallback to IP-based geolocation
          fallbackToIPGeolocation();
        }
      );
    } else {
      // Browser doesn't support geolocation, use IP fallback
      console.log('Browser geolocation not supported, using IP geolocation');
      fallbackToIPGeolocation();
    }
  }, [dispatch, hasLocationParams, paramLat, paramLng]);

  // Handle map move end - fetch pins for new area
  const handleMoveEnd = useCallback((evt) => {
    const { longitude, latitude } = evt.viewState;
    dispatch(setMapCenter(latitude, longitude));
    // Fetch new pins for this area
    dispatch(fetchNearbyPinsRequest(latitude, longitude, 10));
  }, [dispatch]);

  // Handle pin click
  const handlePinClick = useCallback((pin, e) => {
    e.originalEvent.stopPropagation();
    dispatch(selectPin(pin));
  }, [dispatch]);


  // Handle mouse down - store coords
  const handleMouseDown = useCallback((event) => {
    mouseDownPos.current = { x: event.point.x, y: event.point.y };
    clickCoords.current = event.lngLat;
    isDragging.current = false;
  }, []);

  // Handle mouse move (detect dragging)
  const handleMouseMove = useCallback((event) => {
    if (mouseDownPos.current) {
      const dx = event.point.x - mouseDownPos.current.x;
      const dy = event.point.y - mouseDownPos.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // If moved more than 3 pixels, consider it a drag
      if (distance > 3) {
        isDragging.current = true;
        clickCoords.current = null; // Clear coords immediately
      }
    }
  }, []);

  // Handle mouse up - if didn't drag, open dialog
  const handleMouseUp = useCallback((event) => {
    // Double-check drag distance at mouseUp time
    let wasDragging = isDragging.current;

    if (mouseDownPos.current && event.point) {
      const dx = event.point.x - mouseDownPos.current.x;
      const dy = event.point.y - mouseDownPos.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // If moved more than 3 pixels total, it was a drag
      if (distance > 3) {
        wasDragging = true;
      }
    }

    // Only open dialog if we have coords, didn't drag, and user is logged in
    if (clickCoords.current && !wasDragging && accessToken && me) {
      setTempPin({
        lng: clickCoords.current.lng,
        lat: clickCoords.current.lat
      });
      setShowAddTrackModal(true);
    } else if (!wasDragging && !accessToken) {
      // Clear selected pin if not logged in and didn't drag
      dispatch(clearSelectedPin());
    }

    // Clean up
    clickCoords.current = null;
    isDragging.current = false;
    mouseDownPos.current = null;
  }, [accessToken, me, dispatch]);

  // Handle "Play" button click in popup
  const handlePlayTrack = useCallback((pin) => {
    // Play the track
    dispatch(playTrackRequest(pin.trackId, pin.tourId));

    // TODO: Load all tracks for the tour for auto-progression
    // This would require fetching the tour's track list

    dispatch(clearSelectedPin());
  }, [dispatch]);

  // Handle city search result selection
  const handleCitySelect = useCallback((cityData) => {
    const { longitude, latitude, cityName } = cityData;

    // Animate map to the selected city
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [longitude, latitude],
        zoom: 13,
        duration: 2000, // 2 second animation
        essential: true, // Animation can be interrupted by user
        curve: 1.42, // Smooth easing curve
      });
    }

    // Update map center in Redux
    dispatch(setMapCenter(latitude, longitude));

    // Fetch pins for the new city
    dispatch(fetchNearbyPinsRequest(latitude, longitude, 10));

    // Clear any selected pin
    dispatch(clearSelectedPin());

    // Optional: Log the city search for analytics
    console.log(`User searched for: ${cityName}`);
  }, [dispatch]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setViewState((prev) => ({
      ...prev,
      zoom: Math.min(prev.zoom + 1, 20)
    }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setViewState((prev) => ({
      ...prev,
      zoom: Math.max(prev.zoom - 1, 0)
    }));
  }, []);

  // Handle logout
  const handleLogout = useCallback(() => {
    dispatch(logout());
    setShowUserMenu(false);
  }, [dispatch]);

  // Handle successful track creation
  const handleTrackAdded = useCallback((trackData) => {
    // Clear temp pin
    setTempPin(null);

    // Refresh pins in the current map area
    const { latitude, longitude } = viewState;
    dispatch(fetchNearbyPinsRequest(latitude, longitude, 10));
  }, [dispatch, viewState]);

  return (
    <div className="map-view-container">
      {/* Google Maps-style navbar with integrated search */}
      <div className="map-navbar">
        {/* Search bar integrated into navbar */}
        <SearchBar onResultSelect={handleCitySelect} />

        <div className="map-nav-actions">
          {accessToken && me ? (
            <>
              <div className="map-user-menu">
                <button
                  className="map-user-btn"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                >
                  <i className="bi bi-person-circle"></i>
                  <span>{me.email?.split('@')[0]}</span>
                </button>
                {showUserMenu && (
                  <div className="map-user-dropdown">
                    <Link to="/tourguide" onClick={() => setShowUserMenu(false)}>
                      Dashboard
                    </Link>
                    <Link to="/tourguide/tours/edit" onClick={() => setShowUserMenu(false)}>
                      Create Tour
                    </Link>
                    <div className="dropdown-divider"></div>
                    <button onClick={handleLogout}>Logout</button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/signin" className="map-nav-btn">Sign In</Link>
              <Link to="/signup" className="map-nav-btn-primary">Sign Up</Link>
            </>
          )}
        </div>
      </div>

      {/* Zoom controls */}
      <div className="zoom-controls">
        <button
          className="zoom-btn zoom-in"
          onClick={handleZoomIn}
          title="Zoom in"
        >
          +
        </button>
        <button
          className="zoom-btn zoom-out"
          onClick={handleZoomOut}
          title="Zoom out"
        >
          −
        </button>
      </div>

      <Map
        ref={mapRef}
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        onMoveEnd={handleMoveEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchMove={handleMouseMove}
        onTouchEnd={handleMouseUp}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: '100%', height: '100vh' }}
      >
        {/* User location marker */}
        {userLocation && (
          <Marker
            longitude={userLocation.lng}
            latitude={userLocation.lat}
            anchor="center"
          >
            <div className="user-location-marker">
              <div className="pulse-ring"></div>
              <div className="user-dot"></div>
            </div>
          </Marker>
        )}

        {/* Temporary pin (before saving) */}
        {tempPin && (
          <Marker
            longitude={tempPin.lng}
            latitude={tempPin.lat}
            anchor="bottom"
          >
            <div
              className="custom-marker temp-marker"
              style={{ backgroundColor: '#4CAF50' }}
            >
              <span className="marker-emoji">📍</span>
            </div>
          </Marker>
        )}

        {/* Audio track pins */}
        {pins.map((pin) => (
          <Marker
            key={pin.trackId}
            longitude={pin.lng}
            latitude={pin.lat}
            anchor="bottom"
            onClick={(e) => handlePinClick(pin, e)}
          >
            <div
              className="custom-marker"
              style={{
                backgroundColor: pin.pinColor || '#FF5733',
              }}
              title={pin.trackTitle}
            >
              <span className="marker-emoji">{pin.pinEmoji || '📍'}</span>
            </div>
          </Marker>
        ))}

        {/* Selected pin popup */}
        {selectedPin && (
          <Popup
            longitude={selectedPin.lng}
            latitude={selectedPin.lat}
            anchor="bottom"
            onClose={() => dispatch(clearSelectedPin())}
            closeButton={true}
            closeOnClick={false}
            offset={25}
          >
            <div className="pin-popup">
              <h3>{selectedPin.trackTitle}</h3>
              <p className="tour-title">{selectedPin.tourTitle}</p>
              <div className="popup-meta">
                <span className="duration">
                  {Math.floor(selectedPin.durationSeconds / 60)}min {selectedPin.durationSeconds % 60}s
                </span>
                {selectedPin.distance !== undefined && (
                  <span className="distance">{selectedPin.distance.toFixed(2)}km away</span>
                )}
              </div>
              <button
                className="play-button"
                onClick={() => handlePlayTrack(selectedPin)}
              >
                ▶ Play
              </button>
            </div>
          </Popup>
        )}
      </Map>

      {/* Loading overlay */}
      {pinsLoading && (
        <div className="map-loading-overlay">
          <div className="spinner"></div>
          <p>Loading tracks...</p>
        </div>
      )}

      {/* Add Track Modal */}
      {tempPin && (
        <MapAddTrackModal
          isOpen={showAddTrackModal}
          onClose={() => {
            setShowAddTrackModal(false);
            setTempPin(null);
          }}
          latitude={tempPin.lat}
          longitude={tempPin.lng}
          onSuccess={handleTrackAdded}
        />
      )}
    </div>
  );
};

export default MapView;
